import { AdminCities } from '@cookspace-shared-contracts/core';

import { baseApi } from '@/api/baseApi';
import { parseWithSchema } from '@/lib/safeParse';

/**
 * RTK Query endpoints для admin-backend /cities/*.
 *
 * Глобальный справочник городов (city-aware, без cafeId).
 * Все ответы парсятся через Zod, чтобы поймать drift контрактов.
 * DELETE отдаёт HTTP 204 без тела — для него парсинг body пропускаем
 * (api-contracts.md): EmptyResponseSchema не принимает null от 204.
 */
export const citiesApi = baseApi.injectEndpoints({
    endpoints: (build) => ({
        listCities: build.query<
            AdminCities.GetCitiesResponse,
            AdminCities.GetCitiesRequest | void
        >({
            query: (params) => {
                const search: Record<string, string> = {};
                if (params?.isActive !== undefined) {
                    search.isActive = params.isActive ? 'true' : 'false';
                }
                return {
                    url: '/cities',
                    method: 'GET',
                    params: search
                };
            },
            transformResponse: (raw: unknown): AdminCities.GetCitiesResponse =>
                parseWithSchema(AdminCities.GetCitiesResponseSchema, raw),
            providesTags: (result) =>
                result
                    ? [
                        ...result.items.map((c) => ({
                            type: 'City' as const,
                            id: c.id
                        })),
                        { type: 'City' as const, id: 'LIST' }
                    ]
                    : [{ type: 'City' as const, id: 'LIST' }]
        }),

        createCity: build.mutation<
            AdminCities.CreateCityResponse,
            AdminCities.CreateCityRequest
        >({
            query: (body) => ({
                url: '/cities',
                method: 'POST',
                body
            }),
            transformResponse: (raw: unknown): AdminCities.CreateCityResponse =>
                parseWithSchema(AdminCities.CreateCityResponseSchema, raw),
            invalidatesTags: [{ type: 'City', id: 'LIST' }]
        }),

        updateCity: build.mutation<
            AdminCities.UpdateCityResponse,
            { id: string; body: AdminCities.UpdateCityRequest }
        >({
            query: ({ id, body }) => ({
                url: `/cities/${id}`,
                method: 'PATCH',
                body
            }),
            transformResponse: (raw: unknown): AdminCities.UpdateCityResponse =>
                parseWithSchema(AdminCities.UpdateCityResponseSchema, raw),
            invalidatesTags: (_result, _error, arg) => [
                { type: 'City', id: arg.id },
                { type: 'City', id: 'LIST' }
            ]
        }),

        deleteCity: build.mutation<
            AdminCities.DeleteCityResponse,
            { id: string }
        >({
            query: ({ id }) => ({
                url: `/cities/${id}`,
                method: 'DELETE'
            }),
            // HTTP 204 — тела нет, парсить нечего (api-contracts.md).
            transformResponse: (): AdminCities.DeleteCityResponse => ({}),
            invalidatesTags: (_result, _error, arg) => [
                { type: 'City', id: arg.id },
                { type: 'City', id: 'LIST' }
            ]
        }),

        reorderCities: build.mutation<
            AdminCities.ReorderCitiesResponse,
            AdminCities.ReorderCitiesRequest
        >({
            query: (body) => ({
                url: '/cities/reorder',
                method: 'POST',
                body
            }),
            transformResponse: (raw: unknown): AdminCities.ReorderCitiesResponse =>
                parseWithSchema(AdminCities.ReorderCitiesResponseSchema, raw),
            invalidatesTags: [{ type: 'City', id: 'LIST' }]
        }),

        // Слияние города-дубля (источник :id в path) в целевой
        // (targetCityId в body), FR-21. Backend перепривязывает зависимости
        // источник→цель и оставляет источник 301-маркером (redirectToSlug +
        // isActive: false). Меняются оба города → инвалидируем оба + LIST.
        mergeCity: build.mutation<
            AdminCities.MergeCityResponse,
            { id: string; body: AdminCities.MergeCityRequest }
        >({
            query: ({ id, body }) => ({
                url: `/cities/${id}/merge`,
                method: 'POST',
                body
            }),
            transformResponse: (raw: unknown): AdminCities.MergeCityResponse =>
                parseWithSchema(AdminCities.MergeCityResponseSchema, raw),
            invalidatesTags: (_result, _error, arg) => [
                { type: 'City', id: arg.id },
                { type: 'City', id: arg.body.targetCityId },
                { type: 'City', id: 'LIST' }
            ]
        })
    })
});

export const {
    useListCitiesQuery,
    useCreateCityMutation,
    useUpdateCityMutation,
    useDeleteCityMutation,
    useReorderCitiesMutation,
    useMergeCityMutation
} = citiesApi;
